import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Crown, Edit, Plus, Trash2, GripVertical, Check, X, Users, UserCheck, DollarSign, Loader2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SubscriptionPlan {
  id: string;
  plan_key: string;
  name: string;
  price: number;
  patient_limit: number | null;
  supervisor_limit: number | null;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

interface PlanFormData {
  plan_key: string;
  name: string;
  price: string;
  patient_limit: string;
  supervisor_limit: string;
  features: string;
  is_active: boolean;
}

const initialFormData: PlanFormData = {
  plan_key: '',
  name: '',
  price: '0',
  patient_limit: '',
  supervisor_limit: '',
  features: '',
  is_active: true,
};

interface SortableRowProps {
  plan: SubscriptionPlan;
  onToggleActive: (plan: SubscriptionPlan) => void;
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: (plan: SubscriptionPlan) => void;
}

function SortableRow({ plan, onToggleActive, onEdit, onDelete }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: plan.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? 'bg-muted/50' : ''}>
      <TableCell>
        <button
          className="cursor-grab active:cursor-grabbing touch-none p-1 rounded hover:bg-muted"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">{plan.name}</span>
          <span className="text-xs text-muted-foreground">{plan.plan_key}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <DollarSign className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium">{plan.price}</span>
          <span className="text-xs text-muted-foreground">/mo</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Users className="h-3 w-3 text-muted-foreground" />
          {plan.patient_limit !== null ? plan.patient_limit.toLocaleString() : 'Unlimited'}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <UserCheck className="h-3 w-3 text-muted-foreground" />
          {plan.supervisor_limit !== null ? plan.supervisor_limit : 'Unlimited'}
        </div>
      </TableCell>
      <TableCell>
        <Badge 
          variant={plan.is_active ? 'default' : 'secondary'}
          className={plan.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}
        >
          {plan.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleActive(plan)}
            title={plan.is_active ? 'Deactivate' : 'Activate'}
          >
            {plan.is_active ? (
              <X className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Check className="h-4 w-4 text-green-600" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(plan)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(plan)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

import { useSearchParams } from 'react-router-dom';

export function PlanManagement() {
  const { user } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(initialFormData);

  // Check for impersonation mode - super admin features should be hidden when impersonating
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const isSuperAdmin = user?.role === 'super_admin' && !isImpersonating;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isSuperAdmin) {
      loadPlans();
    }
  }, [isSuperAdmin]);

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      const mappedPlans: SubscriptionPlan[] = (data || []).map(p => ({
        id: p.id,
        plan_key: p.plan_key,
        name: p.name,
        price: p.price,
        patient_limit: p.patient_limit,
        supervisor_limit: p.supervisor_limit,
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
        is_active: p.is_active,
        sort_order: p.sort_order,
      }));
      setPlans(mappedPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = plans.findIndex((p) => p.id === active.id);
      const newIndex = plans.findIndex((p) => p.id === over.id);
      
      const newPlans = arrayMove(plans, oldIndex, newIndex);
      setPlans(newPlans);

      // Update sort_order in database
      try {
        const updates = newPlans.map((plan, index) => ({
          id: plan.id,
          sort_order: (index + 1) * 10,
        }));

        for (const update of updates) {
          await supabase
            .from('subscription_plans')
            .update({ sort_order: update.sort_order })
            .eq('id', update.id);
        }

        toast.success('Plan order updated');
      } catch (error) {
        console.error('Error updating plan order:', error);
        toast.error('Failed to update plan order');
        loadPlans(); // Reload to restore original order
      }
    }
  };

  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormData({
      ...initialFormData,
      sort_order: String((plans.length + 1) * 10),
    } as any);
    setDialogOpen(true);
  };

  const openEditDialog = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      plan_key: plan.plan_key,
      name: plan.name,
      price: String(plan.price),
      patient_limit: plan.patient_limit !== null ? String(plan.patient_limit) : '',
      supervisor_limit: plan.supervisor_limit !== null ? String(plan.supervisor_limit) : '',
      features: plan.features.join('\n'),
      is_active: plan.is_active,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (plan: SubscriptionPlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.plan_key || !formData.name) {
      toast.error('Plan key and name are required');
      return;
    }

    setSaving(true);
    try {
      const planData = {
        plan_key: formData.plan_key.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        price: parseFloat(formData.price) || 0,
        patient_limit: formData.patient_limit ? parseInt(formData.patient_limit) : null,
        supervisor_limit: formData.supervisor_limit ? parseInt(formData.supervisor_limit) : null,
        features: formData.features.split('\n').filter(f => f.trim()),
        is_active: formData.is_active,
        sort_order: editingPlan?.sort_order || (plans.length + 1) * 10,
      };

      if (editingPlan) {
        const { error } = await supabase
          .from('subscription_plans')
          .update(planData)
          .eq('id', editingPlan.id);

        if (error) throw error;
        toast.success('Plan updated successfully');
      } else {
        const { error } = await supabase
          .from('subscription_plans')
          .insert(planData);

        if (error) throw error;
        toast.success('Plan created successfully');
      }

      setDialogOpen(false);
      loadPlans();
    } catch (error: any) {
      console.error('Error saving plan:', error);
      toast.error(error.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!planToDelete) return;

    setSaving(true);
    try {
      // Check if any agencies are using this plan
      const { data: agenciesUsingPlan } = await supabase
        .from('agencies')
        .select('id, plan')
        .limit(100);

      const agenciesWithPlan = agenciesUsingPlan?.filter(a => a.plan === planToDelete.plan_key);
      
      if (agenciesWithPlan && agenciesWithPlan.length > 0) {
        toast.error('Cannot delete plan: agencies are currently using it');
        setDeleteDialogOpen(false);
        setPlanToDelete(null);
        return;
      }

      const { error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', planToDelete.id);

      if (error) throw error;
      
      toast.success('Plan deleted successfully');
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      loadPlans();
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      toast.error(error.message || 'Failed to delete plan');
    } finally {
      setSaving(false);
    }
  };

  const togglePlanActive = async (plan: SubscriptionPlan) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: !plan.is_active })
        .eq('id', plan.id);

      if (error) throw error;
      
      toast.success(`Plan ${!plan.is_active ? 'activated' : 'deactivated'}`);
      loadPlans();
    } catch (error) {
      console.error('Error toggling plan:', error);
      toast.error('Failed to update plan status');
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              Subscription Plans
            </CardTitle>
            <CardDescription>
              Configure pricing and features for each plan tier. Drag to reorder.
            </CardDescription>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Plan
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Patient Limit</TableHead>
                  <TableHead>Supervisor Limit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={plans.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {plans.map((plan) => (
                      <SortableRow
                        key={plan.id}
                        plan={plan}
                        onToggleActive={togglePlanActive}
                        onEdit={openEditDialog}
                        onDelete={openDeleteDialog}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {plans.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No plans configured. Click "Add Plan" to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
            <DialogDescription>
              {editingPlan ? 'Update the plan configuration' : 'Configure a new subscription plan'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plan_key">Plan Key</Label>
                <Input
                  id="plan_key"
                  value={formData.plan_key}
                  onChange={(e) => setFormData({ ...formData, plan_key: e.target.value })}
                  placeholder="e.g., pro"
                  disabled={!!editingPlan}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Professional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Monthly Price ($)</Label>
              <Input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="patient_limit">Patient Limit</Label>
                <Input
                  id="patient_limit"
                  type="number"
                  min="0"
                  value={formData.patient_limit}
                  onChange={(e) => setFormData({ ...formData, patient_limit: e.target.value })}
                  placeholder="Empty = Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supervisor_limit">Supervisor Limit</Label>
                <Input
                  id="supervisor_limit"
                  type="number"
                  min="0"
                  value={formData.supervisor_limit}
                  onChange={(e) => setFormData({ ...formData, supervisor_limit: e.target.value })}
                  placeholder="Empty = Unlimited"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="features">Features (one per line)</Label>
              <textarea
                id="features"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="Advanced triage&#10;Priority support&#10;Custom branding"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPlan ? 'Save Changes' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the "{planToDelete?.name}" plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
